import React, { memo } from 'react'
import { PurchaseHistoryPageProps } from 'src/pages/PurchaseHistoryPage/index'
import { Container } from 'src/components/common/Container'
import styled, { css } from 'styled-components'
import { Spinner } from 'src/components/common/Spinner'
import { Divider } from 'src/components/common/Divider'
import { PurchaseHistoryListSpace } from 'src/types/api'
import PurchaseHistoryCouponCard from 'src/components/common/PurchaseHistoryCouponCard'
import RenderIf from 'src/components/common/RenderIf'
import { Typography } from 'src/components/common/Typography'
import {
  Noto_body1_m16,
  Noto_body3_r12,
  Noto_subheading_r18,
} from 'src/styles/fonts/NotoSansKrFonts'
import UnderLineTab from 'src/components/common/UnderLineTab'
import { Icon } from 'src/assets/svg/Icon'
import { UnderLineType, useGlobalStore } from 'src/store/zustand'
import usePageNav from 'src/hooks/usePageNav.hook'

const PurchaseHistoryPageView = (props: PurchaseHistoryPageProps) => {
  const { purchaseCouponNoMore, isUsablePossessionCouponCnt, purchaseCouponListData, setTarget } =
    props
  const { purchaseHistoryUnderLineTab, setPurchaseHistoryUnderLineTab } = useGlobalStore()
  const { purchaseHistorySavePageNav } = usePageNav()

  const cntPurchaseCouponList = purchaseCouponListData?.length
  const isEmptyPurchaseCouponList = cntPurchaseCouponList === 0
  const isUsablePurchaseCouponList = purchaseHistoryUnderLineTab === 0

  const handleChangeUnderLine = (value: UnderLineType) => setPurchaseHistoryUnderLineTab(value)

  const CouponPurchaseHistoryContents = memo(() => {
    return (
      <S.PurchaseHistoryContentsContainer $option="flex_column">
        <RenderIf when={!isEmptyPurchaseCouponList && isUsablePurchaseCouponList}>
          <S.PurchaseHistoryStoreUsAbleNotiContainer $option="flex_row">
            <Icon.Noti />
            <S.PurchaseHistoryStoreUsAbleTypography
              $fontStyle={Noto_body3_r12}
              $color="var(--black-50)"
            >
              쿠폰 사용 가능 매장을 꼭 확인하세요!
            </S.PurchaseHistoryStoreUsAbleTypography>
          </S.PurchaseHistoryStoreUsAbleNotiContainer>
        </RenderIf>
        {isEmptyPurchaseCouponList ? (
          <S.IsEmptyPurchaseHistoryContainer $option="flex_column" $row_gap="50px">
            <Typography $color="var(--black-30)" $fontStyle={Noto_subheading_r18}>
              보유한 쿠폰이 없습니다.
            </Typography>
          </S.IsEmptyPurchaseHistoryContainer>
        ) : (
          <Container $option="flex_column">
            {purchaseCouponListData?.map(
              (couponInfo: PurchaseHistoryListSpace.PurchaseListInterface, index: number) => (
                <Container $option="flex_column" key={index}>
                  <PurchaseHistoryCouponCard {...props} couponInfo={couponInfo} />
                  <RenderIf when={purchaseCouponListData?.length - 1 !== index}>
                    <Divider />
                  </RenderIf>
                </Container>
              ),
            )}
          </Container>
        )}
      </S.PurchaseHistoryContentsContainer>
    )
  })

  return (
    <S.PurchaseHistoryWrap $option="flex_column" $isBottomPadding={!isUsablePurchaseCouponList}>
      <UnderLineTab
        items={[
          {
            text: `사용 가능${(isUsablePossessionCouponCnt ?? 0) > 0 ? `(${isUsablePossessionCouponCnt})` : ''}`,
            contents: <CouponPurchaseHistoryContents />,
            value: 0,
          },
          {
            text: '사용완료·만료·취소',
            contents: <CouponPurchaseHistoryContents />,
            value: 1,
          },
        ]}
        state={{
          activeTab: purchaseHistoryUnderLineTab,
          setActiveTab: handleChangeUnderLine,
        }}
        style={S.PurchaseHistoryUnderlineTabStyle}
      />
      <RenderIf when={!purchaseCouponNoMore}>
        <Spinner $color="var(--black-10)" ref={setTarget} $margin="50px auto 0 auto" />
      </RenderIf>
      <RenderIf when={isUsablePurchaseCouponList}>
        <S.PurchaseHistorySaveButton onClick={purchaseHistorySavePageNav}>
          쿠폰 구매내역 {isEmptyPurchaseCouponList ? '불러오기' : '저장하기'}
        </S.PurchaseHistorySaveButton>
      </RenderIf>
    </S.PurchaseHistoryWrap>
  )
}
export default PurchaseHistoryPageView

const S = {
  PurchaseHistoryWrap: styled(Container)<{ $isBottomPadding: boolean }>`
    margin: 0 auto;
    padding-bottom: ${({ $isBottomPadding }) => ($isBottomPadding ? 30 : 0)}px;
    flex: 1;
    background-color: var(--black-4);
  `,
  IsEmptyPurchaseHistoryContainer: styled(Container)`
    flex: 1;
    align-items: center;
    justify-content: center;
    ${Noto_subheading_r18};
  `,
  PurchaseHistoryContentsContainer: styled(Container)`
    flex: 1;
    background-color: var(--black-4);
  `,
  PurchaseHistoryUnderlineTabStyle: css`
    width: calc(100% - 40px);
    margin: 0 auto;
  `,
  PurchaseHistoryStoreUsAbleNotiContainer: styled(Container)`
    width: calc(100% - 20px);
    margin: 10px auto;
    text-align: center;
    border-radius: 6px;
    background: var(--common-transparent-bk-t-5, rgba(0, 0, 0, 0.05));
    justify-content: center;
    align-items: center;
  `,
  PurchaseHistoryStoreUsAbleTypography: styled(Typography)`
    padding: 6px;
    display: block;
  `,
  PurchaseHistorySaveButton: styled.button`
    ${Noto_body1_m16};
    margin: 20px 10px;
    padding: 12px;
    width: calc(100% - 20px);
    border: 1px solid var(--black-10);
    border-radius: 12px;
    background-color: var(--white);
    color: var(--black-60);
  `,
}
